import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
interface ImageModalProps {
  isOpen: boolean;
  image: string;
  title: string;
  category: string;
  type?: 'image' | 'video';
  currentIndex: number;
  totalImages: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}
export function ImageModal({
  isOpen,
  image,
  title,
  category,
  type = 'image',
  currentIndex,
  totalImages,
  onClose,
  onNext,
  onPrev
}: ImageModalProps) {
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    // Lock body scroll when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, onNext, onPrev]);
  return <AnimatePresence data-id="element-559">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 md:p-8" onClick={onClose} data-id="element-560">
          {/* Close Button */}
          <button onClick={onClose} className="absolute top-4 right-4 md:top-8 md:right-8 text-white/70 hover:text-[#DDA247] transition-colors z-50 p-2" data-id="element-561">
            <X className="w-8 h-8" data-id="element-562" />
          </button>

          {/* Navigation Arrows */}
          <button onClick={e => {
        e.stopPropagation();
        onPrev();
      }} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#DDA247] transition-colors z-50 p-2 hidden md:block" data-id="element-563">
            <ChevronLeft className="w-10 h-10" data-id="element-564" />
          </button>

          <button onClick={e => {
        e.stopPropagation();
        onNext();
      }} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#DDA247] transition-colors z-50 p-2 hidden md:block" data-id="element-565">
            <ChevronRight className="w-10 h-10" data-id="element-566" />
          </button>

          {/* Image Container */}
          <div className="relative max-w-7xl max-h-[90vh] w-full flex flex-col items-center justify-center" onClick={e => e.stopPropagation()} data-id="element-567">
            {type === 'video' ? <motion.video key={image} initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} src={encodeURI(image)} controls autoPlay className="max-h-[80vh] w-auto object-contain shadow-2xl border border-white/10" data-id="element-568" /> : <motion.img key={image} initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} src={image} alt={title} className="max-h-[80vh] w-auto object-contain shadow-2xl border border-white/10" data-id="element-569" />}

            <div className="mt-6 text-center" data-id="element-570">
              <span className="text-[#DDA247] text-xs font-bold uppercase tracking-widest mb-2 block" data-id="element-571">
                {category}
              </span>
              <h3 className="text-white text-xl font-bold mb-2" data-id="element-572">{title}</h3>
              <p className="text-gray-500 text-sm tracking-wider" data-id="element-573">
                {currentIndex + 1} / {totalImages}
              </p>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}